import { FinalSaveStatus } from './final-save-status.js';
import { useBrands } from '#~/device/registry/hooks.js';
import { Button } from '@studio/design-system/button';
import * as Dialog from '@studio/design-system/dialog';
import { AnimatePresence, motion, Motions } from '@studio/design-system/motion';
import type { HtmlAttributes } from '@studio/design-system/props';
import { Spinner } from '@studio/design-system/spinner';
import type { ComponentType, ReactElement } from 'react';
import { createElement, useEffect } from 'react';
import { twMerge } from 'cn';
import { resolveModalTitle, shouldShowStepper } from './morph-state.js';
import { Panel as ControlPanel } from './control/index.js';
import { IdentificationDialogs } from './identification/index.js';
// ENG-4159 relocated brand/model onto the RTSP video screen for RTSP-Assist
// flows (RTSP / ONVIF / manual RTSP). The Manufacturer panel is STILL mounted
// for non-RTSP sources without a modelId (e.g. NDI) — those need the classic
// driver + nickname screen, which RTSP Assist does not cover. The Video footer
// routes to it conditionally; see video/footer.tsx.
import { Panel as ManufacturerPanel } from './manufacturer/index.js';
import { AddSourceAuthModal } from './source/dialogs/credentials-dialog.js';
import { OnvifAuthPrompt } from './source/dialogs/onvif-auth-prompt.jsx';
// Pure helper picks the prompt copy variant (ONVIF vs Iris-enabled)
// from the paused status + bulkDiscoveries mirror. Kept in a sibling
// file so the scaffold test can stay focused on layout/state-machine
// and the kind-derivation logic gets its own table-driven test.
// Per `reference-rules-in-comments` (ai.knowledge/global).
import { deriveOnvifAuthPromptKind } from './source/dialogs/onvif-auth-prompt-kind.js';
import { Panel as SourcePanel } from './source/index.js';
import { Steps } from './steps.jsx';
import { useProducer, useStore } from './store/hooks.js';
import { Panel } from './store/index.js';
import { requestCancellation } from './store/bulk-add.slice.js';
import { Panel as VideoPanel } from './video/index.js';
import { useVirtualPageview } from '#~/analytics/pageview/index.js';
import { WORKFLOW_MAPPINGS } from '#~/analytics/pageview/constants.js';
// Multi-Add Phase 2 (PRD §2.10) — window-close confirmation. The hook
// adds a `beforeunload` listener only while a bulk-add batch is in
// flight, so non-bulk routes don't carry the warning.
// Per `architecture-principles` (Electron + browser parity).
import { isBulkAddInFlight, useWindowCloseGuard } from '#~/electron/window-close-handler.js';
import type { WorkflowMappingKey } from '#~/analytics/pageview/types.js';
import { Sel, STagNew } from '#~/stag/tag.js';
import { useDiscoveryWarning } from './source/use-discovery-warning.js';

// The wizard advances Source → Video → (Manufacturer) → Control. The
// Manufacturer (driver + nickname) panel is only reached for non-RTSP sources
// without a modelId (video/footer.tsx routes there); RTSP-Assist flows skip it
// (brand/model lives on the RTSP video screen). `Partial` because the active
// panel is never Panel.Manufacturer for the RTSP path — the lookup guards it.
const PanelComponents: Partial<Record<Panel, ComponentType<HtmlAttributes>>> = {
  [Panel.Source]: SourcePanel,
  [Panel.Video]: VideoPanel,
  [Panel.Manufacturer]: ManufacturerPanel,
  [Panel.Control]: ControlPanel
};

/**
 * Pure-function helper: derive the progress label shown in the bulk
 * commit overlay from the current `BulkAddStatus`. Extracted so the
 * Scaffold render stays declarative and the wording lives in one
 * place. Per Multi-Add Phase 1 manual QA (sequential pipeline UX).
 */
const bulkStatusLabel = (status: {
  kind: string;
  name?: string;
  index?: number;
  total?: number;
}): string | undefined => {
  if (status.kind === 'preparing') return 'Preparing…';
  if (status.kind === 'configuring' && status.name && status.index && status.total) {
    return `Configuring ${status.name} (${status.index}/${status.total})…`;
  }
  if (status.kind === 'committing' && status.total) {
    return `Adding ${status.total} ${status.total === 1 ? 'source' : 'sources'}…`;
  }
  return undefined;
};

/** Add video `<Scaffold>` component. */
export const Scaffold = ({ className, ...props }: HtmlAttributes): ReactElement => {
  useDiscoveryWarning();
  const panel = useStore((µ) => µ.panel);
  const flowId = useStore((µ) => µ.flowId);
  // bulkMode hides the stepper and pluralizes the title.
  // Per `reference-rules-in-comments` (ai.knowledge/global).
  const bulkMode = useStore(({ source }) => source.bulkMode);
  // Bulk commit lifecycle status. Drives the panel-replacement overlay
  // (spinner + "Configuring…" label) and the nested ONVIF auth prompt.
  // Per Multi-Add Phase 1 manual QA (sequential probe + pause-for-auth).
  const bulkAddStatus = useStore(({ source }) => source.bulkAddStatus);
  // Discriminate the OnvifAuthPrompt copy strings between ONVIF
  // (default) and Iris-enabled. The status payload carries only a
  // `name`, so we look up the paused discovery in the store's
  // bulkDiscoveries mirror (selector.tsx mirrors raw discoveries here
  // including Iris-enabled rows). Read at this top-level so the hook
  // doesn't run conditionally inside the prompt branch below.
  // Per `reference-rules-in-comments` (ai.knowledge/global).
  const bulkDiscoveries = useStore(({ source }) => source.bulkDiscoveries);
  // Multi-Add Phase 2 (PRD §2.10) — cancellation flag. Read here so
  // the Cancel button can flip its label / disable after the user has
  // clicked (the loop break is deferred to the next iteration; the
  // button must not invite a second click).
  // Per `reference-rules-in-comments` (ai.knowledge/global).
  const cancellationRequested = useStore(({ source }) => source.cancellationRequested);
  const producer = useProducer();

  // Intercom preview state — hides chrome that conflicts with the
  // Intercom editor overlay. Brought in from origin/main during the
  // Phase 2 → main merge (2026-05-15).
  const isIntercomPreview = Dialog.useIntercomEditorActive();

  // ENG-3796: when the first-run funnel owns this workflow, report each panel
  // the user reaches so the onboarding funnel keeps a row per screen. The
  // scaffold reports a PANEL and nothing more — what that means in onboarding's
  // taxonomy is decided in onboarding/add-handoff.ts, not here.
  const onboarding = useStore((µ) => µ.onboarding);
  const reportPanelView = onboarding?.onPanelView;

  // While a save is in flight the operator must not close the dialog and lose sight of the outcome.
  // The panel never sets closeOnEscape, so Escape is already suppressed; disabling the explicit ✕
  // closes the remaining affordance for the pending window. handleBeforeClose stays as the backstop.
  const savePending = useStore((µ) => µ.finalSave?.status === 'pending');

  useEffect(() => {
    reportPanelView?.(panel);
  }, [panel, reportPanelView]);

  // Cancel button is visible during the bulk-progress overlay
  // (preparing / configuring / committing) and disabled during
  // `awaiting-auth` (the auth prompt owns user focus there — its Skip
  // control is the cancellation surface for that sub-state). Once the
  // user has clicked Cancel, the button stays visible but disabled and
  // re-labels to "Cancelling…" so the user has visible feedback while
  // the in-flight iteration settles.
  //
  // Esc key does NOT cancel — only explicit button click. We satisfy
  // this by NOT wiring a key handler on the overlay; the dialog's
  // Esc-key path is intercepted by the existing `handleBeforeClose` in
  // wiring.ts, which routes through `tryBulkDiscard` for source-panel
  // discard, NOT through cancellation.
  const showCancelButton =
    bulkAddStatus.kind === 'preparing' ||
    bulkAddStatus.kind === 'configuring' ||
    bulkAddStatus.kind === 'committing' ||
    bulkAddStatus.kind === 'awaiting-auth';
  const cancelDisabled = cancellationRequested || bulkAddStatus.kind === 'awaiting-auth';

  // Arm the window-close confirmation guard whenever a batch is in
  // flight. The hook auto-removes the listener on unmount or when the
  // batch resolves to idle/done. Decoupled from the Cancel button:
  // the user can either click Cancel (graceful) or attempt window-
  // close (renderer dies; cloud-side commits survive).
  useWindowCloseGuard(isBulkAddInFlight(bulkAddStatus));

  // Preload brands list — consumed by the brand/model selectors now living on
  // the RTSP video screen (relocated from the removed Manufacturer panel).
  useBrands();

  // Resolve the component for the current panel. `Partial` map: Manufacturer is
  // absent for RTSP-Assist flows, so the lookup can return undefined (guarded).
  const ActivePanel = PanelComponents[panel];

  // Panel → canonical virtual screen. useVirtualPageview owns the fire-once
  // rule and keys it on the canonical path, so a panel switch that resolves to
  // the same screen no longer emits a second $pageview.
  let mappingKey: WorkflowMappingKey | undefined;

  if (panel === Panel.Source) {
    mappingKey = 'video-source-discover';
  } else if (panel === Panel.Video) {
    mappingKey = 'video-source-configure-rtsp'; // Default to RTSP, could be refined based on protocol
  } else if (panel === Panel.Manufacturer) {
    mappingKey = 'video-source-select-driver';
  } else if (panel === Panel.Control) {
    mappingKey = 'video-source-connect-controls';
  }

  const mapping = mappingKey === undefined ? undefined : WORKFLOW_MAPPINGS[mappingKey];

  useVirtualPageview(mapping?.path ?? null, true, {
    ...(mapping === undefined ? {} : { category: mapping.category }),
    ...(mapping?.section === undefined ? {} : { section: mapping.section }),
    flow_id: flowId
  });

  return (
    // Modal chrome width: `w-[1200px]` is sized for the two-column
    // single-add layout (Selector left + Preview/BridgeSelector right).
    // In bulk mode content.tsx suppresses the right column
    // (showPreview === false), so the chrome narrows to `w-[860px]` to
    // track the visible one-column discovery Table. Width chosen so the
    // 6 grid columns (checkbox + name + ip + protocol + brand + bridge)
    // still breathe without horizontal clipping at typical row content.
    // Both modes stay inside small desktop windows; discovery must be
    // reachable before the first checkbox switches the wizard to bulk mode.
    // When the Intercom editor is active the chrome falls back to a
    // viewport-responsive size so the editor overlay doesn't clip the
    // modal. Per `reference-rules-in-comments` (ai.knowledge/global).
    <div
      className={twMerge(
        'flex max-w-[calc(100vw-64px)] flex-col',
        // Keep the preferred desktop widths, with viewport-aware height
        // and scrolling in both discovery modes.
        bulkMode
          ? 'min-h-[min(800px,calc(100vh-40px))] max-h-[calc(100vh-40px)] w-[860px] gap-28 overflow-y-auto transition-[width] duration-200'
          : isIntercomPreview
            ? 'h-[min(800px,calc(100vh-120px))] w-[min(1200px,calc(100vw-140px))] overflow-auto'
            : 'min-h-[min(800px,calc(100vh-40px))] max-h-[calc(100vh-40px)] w-[1200px] gap-28 overflow-y-auto',
        className
      )}
      {...props}
    >
      {/* Header value for popover/modal title. Title resolves via the
          pure `resolveModalTitle` helper (morph-state.ts) — pluralizes
          when bulk mode is active. */}
      <Dialog.Header closeable={false} title={resolveModalTitle(bulkMode)}>
        {/* Per rule: playwright-stag-selectors-only (ai.knowledge/platform) — stable dialog cleanup */}
        <Dialog.Dismiss
          disabled={savePending}
          {...STagNew(Sel.Dialogs, 'dismiss', 'add-video-source')}
        />
      </Dialog.Header>

      {/* Steps for the wizard. Hidden in bulk mode — there's only one step
          (commit) and the multi-step config panels (Video / Manufacturer /
          Control) are bypassed entirely. */}
      {shouldShowStepper(bulkMode) && <Steps className='px-40' panel={panel} />}

      <FinalSaveStatus />

      {/* "My camera isn't listed" (C1/C2) + "Basic controls" (D1). Mounted once,
          outside the per-panel fade, so its step-advance watcher sees every
          panel change. Inert in bulk mode. */}
      <IdentificationDialogs />

      {/* Animate the different panels and their content */}
      {/*
        Body — three render modes:
          1. Normal panel content (current panel component + the
             single-add credentials modal).
          2. Bulk commit in progress (preparing / configuring /
             committing) → replace the panel with a centred spinner +
             status label. Same UX shape the pre-monorepo PR #1100
             used: the user sees their click is being processed and
             which source the pipeline is on. Per Multi-Add Phase 1
             manual QA.
          3. Bulk commit paused on ONVIF auth → render the
             `<OnvifAuthPrompt>` ON TOP of the spinner (the spinner
             stays mounted in the background so the user keeps the
             "in flight" context). The prompt resolves the pipeline's
             pending promise on Skip/Continue.
       */}
      <AnimatePresence>
        {bulkAddStatus.kind === 'idle' || bulkAddStatus.kind === 'done' ? (
          <motion.div className='flex min-h-0 grow' key={panel} {...Motions.Fade}>
            {/* Guarded lookup: the Partial map can return undefined (Manufacturer
                is absent on the RTSP-Assist path). */}
            {ActivePanel && createElement(ActivePanel)}
            <AddSourceAuthModal />
          </motion.div>
        ) : (
          <motion.div
            className='flex grow flex-col items-center justify-center gap-16 text-foreground'
            key='bulk-progress'
            {...Motions.Fade}
          >
            <Spinner className='h-40 w-40' />
            <span className='text-base'>{bulkStatusLabel(bulkAddStatus) ?? 'Working…'}</span>
            {bulkAddStatus.kind === 'awaiting-auth' && (
              <OnvifAuthPrompt
                name={bulkAddStatus.name}
                index={bulkAddStatus.index}
                total={bulkAddStatus.total}
                onDecision={bulkAddStatus.resolve}
                // Discriminate copy by the type of source currently
                // paused. ONVIF (default) keeps the legacy "ONVIF
                // camera" copy; Iris-enabled swaps to generic Iris-
                // paired-creds wording. The status payload may carry
                // an explicit `promptKind` (forward-compat with
                // wiring.ts), otherwise we derive it by looking up
                // the paused discovery in the store's bulkDiscoveries
                // mirror and reading the `irisEnabled` flag. Defaults
                // to 'onvif' when no match — backward-compatible.
                kind={deriveOnvifAuthPromptKind(bulkAddStatus, bulkDiscoveries)}
              />
            )}
            {/*
              Multi-Add Phase 2 (PRD §2.10) — Cancel button. Click flips
              `cancellationRequested` in the store; the loop in
              `runBulkCommit` checks the flag at the top of each
              iteration and breaks gracefully. The in-flight per-device
              commit is allowed to settle (no kill — `addDevice` /
              `linkAgent` are not reversible). Already-committed devices
              remain in the room.
              Per `reference-rules-in-comments` (ai.knowledge/global).
            */}
            {showCancelButton && (
              <Button
                disabled={cancelDisabled}
                onClick={() => {
                  producer((draft) => {
                    requestCancellation(draft);
                  });
                }}
                variant='secondary'
              >
                {cancellationRequested ? 'Cancelling…' : 'Cancel'}
              </Button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
